import type { FC } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "flowbite-react";
import { HiCheck, HiX } from "react-icons/hi";
import { Button, Dialog } from "@components";
import { cn, Text } from "@design-system";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useOnboardingStatus, useGlobalOrders } from "../../hooks";

interface ChecklistItem {
  id: string;
  title: string;
  completed: boolean;
  locked: boolean;
  lockedReason?: string;
  actionLabel: string;
  onAction: () => void;
}

const dismissKey = (workspaceId: string) =>
  `onboardingChecklistDismissed:${workspaceId}`;

/**
 * In-app onboarding shown on the dashboard after the initial (company) wizard.
 * Guides the user through the remaining setup in dependency order so they never
 * hit a dead-end: the "create first order" step stays locked (with a reason)
 * until a driver exists.
 *
 * Progress is derived from live workspace data, so items tick off automatically
 * as the user creates entities. It hides once the first order is created, and
 * can be dismissed per-workspace.
 */
export const SetupChecklist: FC = () => {
  const { t } = useTranslation(["onboarding", "common"]);
  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspace();
  const status = useOnboardingStatus();
  const ordersQuery = useGlobalOrders();

  const workspaceId = activeWorkspace?.workspaceId ?? "";
  const hasOrder = (ordersQuery.data ?? []).length > 0;

  const [dismissed, setDismissed] = useState(
    () => window.localStorage.getItem(dismissKey(workspaceId)) === "true",
  );
  const [showDismissConfirmation, setShowDismissConfirmation] = useState(false);

  const handleDismiss = () => {
    setDismissed(true);
    window.localStorage.setItem(dismissKey(workspaceId), "true");
    setShowDismissConfirmation(false);
  };

  const items = useMemo<ChecklistItem[]>(() => {
    const lockedOrderReason = t("onboarding:checklist.locked.order");
    const addLabel = t("onboarding:checklist.actions.add");
    return [
      {
        id: "company",
        title: t("onboarding:checklist.items.company.title"),
        completed: status.hasCompany,
        locked: false,
        actionLabel: addLabel,
        onAction: () => navigate("/workspace/companies"),
      },
      {
        id: "vehicle",
        title: t("onboarding:checklist.items.vehicle.title"),
        completed: status.hasVehicle,
        locked: false,
        actionLabel: addLabel,
        onAction: () => navigate("/resources/vehicles?new=true"),
      },
      {
        id: "driver",
        title: t("onboarding:checklist.items.driver.title"),
        completed: status.hasDriver,
        locked: false,
        actionLabel: addLabel,
        onAction: () => navigate("/resources/drivers?new=true"),
      },
      {
        id: "customer",
        title: t("onboarding:checklist.items.customer.title"),
        completed: status.hasCustomer,
        locked: false,
        actionLabel: addLabel,
        onAction: () => navigate("/customers?new=true"),
      },
      {
        id: "product",
        title: t("onboarding:checklist.items.product.title"),
        completed: status.hasProduct,
        locked: false,
        actionLabel: addLabel,
        onAction: () => navigate("/resources/products?new=true"),
      },
      {
        id: "order",
        title: t("onboarding:checklist.items.order.title"),
        completed: hasOrder,
        locked: !status.canCreateOrder,
        lockedReason: lockedOrderReason,
        actionLabel: addLabel,
        onAction: () => navigate("/orders/new"),
      },
    ];
  }, [t, navigate, status, hasOrder]);

  const completedItems = items.filter((item) => item.completed).length;
  const progressPercentage =
    items.length > 0 ? Math.round((completedItems / items.length) * 100) : 0;

  // Hide until initial setup is done, while data resolves, once dismissed, or
  // after the first order is created (the goal of the checklist).
  if (
    status.isResolving ||
    status.requiresInitialSetup ||
    dismissed ||
    hasOrder
  ) {
    return null;
  }

  return (
    <>
      <div className="mb-4 rounded-lg border border-border-subtle bg-surface p-4 shadow-custom dark:bg-surface">
        <div className="flex items-center justify-between gap-4">
          <Text as="h2" variant="section-title">
            {t("onboarding:checklist.title")}
          </Text>
          <div className="flex items-center gap-3">
            <Text variant="muted" className="whitespace-nowrap text-sm">
              {t("onboarding:checklist.progress", {
                completed: completedItems,
                total: items.length,
              })}
            </Text>
            <button
              type="button"
              onClick={() => setShowDismissConfirmation(true)}
              aria-label={t("onboarding:checklist.dismiss")}
              className="rounded p-1 text-muted hover:bg-surface-hover hover:text-text"
            >
              <HiX className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          className="mt-3 h-2 overflow-hidden rounded-full bg-surface-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPercentage}
          aria-label={t("onboarding:checklist.progress", {
            completed: completedItems,
            total: items.length,
          })}
        >
          <div
            className="h-full rounded-full bg-brand transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <ul className="mt-2 flex flex-col divide-y divide-border">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex min-h-[52px] items-center justify-between gap-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className={cn(
                    "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2",
                    item.completed
                      ? "border-brand bg-brand text-white"
                      : "border-border-strong bg-surface",
                    item.locked && "opacity-50",
                  )}
                >
                  {item.completed ? <HiCheck className="h-4 w-4" /> : null}
                </span>
                <Text
                  variant="body"
                  className={cn(
                    "truncate text-sm font-medium",
                    item.completed && "text-muted line-through",
                  )}
                >
                  {item.title}
                </Text>
              </div>

              <div className="flex-shrink-0">
                {item.completed ? null : item.locked ? (
                  <Tooltip content={item.lockedReason}>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-24 justify-center"
                      disabled
                    >
                      {item.actionLabel}
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-24 justify-center"
                    onClick={item.onAction}
                  >
                    {item.actionLabel}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <Dialog
        show={showDismissConfirmation}
        size="md"
        title={t("onboarding:checklist.dismiss_confirmation.title")}
        content={
          <Text variant="secondary">
            {t("onboarding:checklist.dismiss_confirmation.description")}
          </Text>
        }
        buttonConfirmText={t(
          "onboarding:checklist.dismiss_confirmation.confirm",
        )}
        buttonCloseText={t("common:cancel")}
        toggleModal={() => setShowDismissConfirmation(false)}
        confirmButton={handleDismiss}
      />
    </>
  );
};

export default SetupChecklist;
